import React, {useState, useEffect} from 'react';
import {DateTime} from 'luxon';
import Spinner from '@/components/ui/Spinner';
import {BaseModal} from '@/components/modals';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

export interface TeamAssignment {
    id: number;
    teamId: number;
    teamName: string;
    teamShortname: string;
    startDate: string | null;
    endDate: string | null;
    isActive: boolean;
}

export interface AgentTeamAssignmentEditModalProps {
    show: boolean;
    assignment: TeamAssignment | null;
    onSave: (assignment: TeamAssignment) => void;
    onClose: () => void;
    saving: boolean;
}

const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) {return '';}
    try {
        return DateTime.fromISO(dateString).toFormat('yyyy-MM-dd');
    } catch {
        return '';
    }
};

const AgentTeamAssignmentEditModal: React.FC<AgentTeamAssignmentEditModalProps> = ({
    show,
    assignment,
    onSave,
    onClose,
    saving
}) => {
    const {showError} = useAlert();
    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        isActive: false
    });

    useEffect(() => {
        if (assignment) {
            setFormData({
                startDate: formatDateForInput(assignment.startDate),
                endDate: formatDateForInput(assignment.endDate),
                isActive: assignment.isActive
            });
        }
    }, [assignment]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const nativeEvent = e.nativeEvent as SubmitEvent;
        const submitter = nativeEvent.submitter as HTMLElement | null;
        
        if (submitter) {
            const submitButton = document.querySelector('button[form="team-assignment-form"][type="submit"]') as HTMLButtonElement;
            if (submitButton && submitter !== submitButton && !submitButton.contains(submitter)) {
                return;
            }
        }

        if (assignment) {
            if (!formData.startDate) {
                showError(i18n.t('jt_start_date_required.text', 'Start date is required'));
                return;
            }

            const updatedAssignment: TeamAssignment = {
                ...assignment,
                startDate: formData.startDate,
                endDate: formData.endDate || null,
                isActive: formData.isActive
            };
            onSave(updatedAssignment);
        }
    };

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!assignment) {return null;}

    return (
        <BaseModal
            isOpen={show}
            onClose={onClose}
            title={i18n.t('jt_edit_team_assignment.text', 'Edit Team Assignment')}
            size="lg"
            footer={
                <div className="d-flex">
                    <button
                        type="button"
                        className="btn btn-secondary m-2"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {i18n.t('jt_cancel.text', 'Cancel')}
                    </button>
                    <button
                        type="submit"
                        form="team-assignment-form"
                        className="btn btn-primary m-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Spinner variant="border" size="sm" className="mr-2" text={i18n.t('sp_saving.text')} centered />
                            </>
                        ) : (
                            i18n.t('jt_save_changes.text', 'Save Changes')
                        )}
                    </button>
                </div>
            }
        >
            <form id="team-assignment-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                    <label className="form-label">
                        <strong>{i18n.t('th_team.text', 'Team')}:</strong>
                    </label>
                    <p className="form-control-plaintext">
                        {assignment.teamName} ({assignment.teamShortname})
                    </p>
                </div>

                <div className="mb-3">
                    <label htmlFor="startDate" className="form-label">
                        {i18n.t('th_start_date.text', 'Start Date')}
                    </label>
                    <input
                        type="date"
                        className="form-control"
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label htmlFor="endDate" className="form-label">
                        {i18n.t('th_end_date.text', 'End Date')}
                    </label>
                    <input
                        type="date"
                        className="form-control"
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                    <div className="form-text">
                        {i18n.t('jt_leave_empty_for_ongoing.text', 'Leave empty for ongoing assignment')}
                    </div>
                </div>

                <div className="mb-3">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="isActive">
                            {i18n.t('jt_active_assignment.text', 'Active Assignment')}
                        </label>
                    </div>
                    <div className="form-text">
                        {i18n.t('jt_only_one_active_assignment.text', 'Only one assignment can be active at a time')}
                    </div>
                </div>
            </form>
        </BaseModal>
    );
};

export default AgentTeamAssignmentEditModal;
